/**
 * dbproxy — dashboard stránka
 *
 * Záložky: Připojení | Explorer
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Tab, TabList, TabPanel, Tabs } from '../../components/ui/Tabs';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { StatusDot } from '../../components/ui/StatusDot';
import { EmptyState } from '../../components/ui/EmptyState';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import {
  ChevronRight,
  Database,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Server,
  Settings,
  Table as TableIcon,
  Trash2,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../../lib/i18n';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Connection {
  id: string;
  label: string;
  dialect: 'mssql' | 'postgres' | 'mysql';
  host: string;
  port: number;
  database: string;
  username: string;
  mode: 'read-only' | 'read-write';
  options: Record<string, unknown>;
  tenant: string | null;
  enabled: boolean;
  password_set: boolean;
  created_at: string;
}

interface IntrospectRow {
  schema?: string;
  name?: string;
  table?: string;
  column?: string;
  data_type?: string;
  is_nullable?: string;
  position?: number;
  [key: string]: unknown;
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function DbProxyPage() {
  const { t } = useTranslation();
  return (
    <div className='space-y-6'>
      <PageHeader
        title='DB Proxy'
        description={t('dbproxy.multiTenantProxyExternim', 'Multi-tenant proxy k externím SQL databázím — heslo v SecretStore, scope-gated CRUD')}
        icon={<Database size={24} />}
      />
      <Tabs defaultTab='connections'>
        <TabList>
          <Tab id='connections' icon={<Server size={16} />}>{t('dbproxy.pripojeni', 'Připojení')}</Tab>
          <Tab id='explorer' icon={<Search size={16} />}>Explorer</Tab>
        </TabList>
        <TabPanel id='connections' keepMounted>
          <ConnectionsTab />
        </TabPanel>
        <TabPanel id='explorer' keepMounted>
          <ExplorerTab />
        </TabPanel>
      </Tabs>
    </div>
  );
}

// ── Connections Tab ───────────────────────────────────────────────────────────

function ConnectionsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState<Connection | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['dbproxy-connections'],
    queryFn: () =>
      api.mcp<{ ok: boolean; connections?: Connection[] }>('dbproxy.connections', { action: 'list' }),
    staleTime: 15_000,
  });
  const conns = data?.connections ?? [];

  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      api.mcp<{ ok: boolean; error?: string }>('dbproxy.connections', { action: 'remove', id }),
    onSuccess: (res) => {
      const r = res as { ok: boolean; error?: string };
      if (!r.ok) {
        toast.error(`Smazání selhalo: ${r.error ?? i18n.t('common.unknownError', 'neznámá chyba')}`);
        return;
      }
      toast.success(i18n.t('dbproxy.pripojeniSmazano', 'Připojení smazáno'));
      qc.invalidateQueries({ queryKey: ['dbproxy-connections'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const testMut = useMutation({
    mutationFn: (id: string) =>
      api.mcp<{ ok: boolean; error?: string; latency_ms?: number }>('dbproxy.connections', {
        action: 'test',
        id,
      }),
    onSuccess: (r) => {
      const res = r as { ok: boolean; error?: string; latency_ms?: number };
      if (res.ok) toast.success(`Připojení OK (${res.latency_ms}ms)`);
      else toast.error(`Test selhal: ${res.error}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className='flex items-center justify-between'>
          <span className='flex items-center gap-2'>
            <Server size={18} /> {t('dbproxy.pripojeni', 'Připojení')} <Badge>{conns.length}</Badge>
          </span>
          <div className='flex gap-2'>
            <Button size='sm' variant='ghost' onClick={() => refetch()}>
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </Button>
            <Button size='sm' variant='primary' onClick={() => setShowAdd(true)}>
              <Plus size={14} /> {t('ui.add', 'Přidat')}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {conns.length === 0 && !isLoading && (
          <EmptyState icon={<Server size={32} />} title={t('dbproxy.zadnaPripojeniPridejtePrvni', 'Žádná připojení — přidejte první')} />
        )}
        {conns.length > 0 && (
          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b'>
                  <th className='text-left p-2'>Stav</th>
                  <th className='text-left p-2'>Label</th>
                  <th className='text-left p-2'>Dialekt</th>
                  <th className='text-left p-2'>Host : Port / DB</th>
                  <th className='text-left p-2'>{t('dbproxy.rezim', 'Režim')}</th>
                  <th className='text-left p-2'>Tenant</th>
                  <th className='text-right p-2'>Akce</th>
                </tr>
              </thead>
              <tbody>
                {conns.map((c) => (
                  <tr key={c.id} className='border-b hover:bg-gray-50'>
                    <td className='p-2'>
                      <StatusDot status={c.enabled ? 'running' : 'stopped'} />
                    </td>
                    <td className='p-2 font-medium'>{c.label}</td>
                    <td className='p-2'>
                      <Badge>{c.dialect.toUpperCase()}</Badge>
                    </td>
                    <td className='p-2 font-mono text-xs'>
                      {c.host}:{c.port} / {c.database}
                    </td>
                    <td className='p-2'>
                      <Badge
                        className={c.mode === 'read-only' ? 'bg-amber-100 text-amber-800' : ''}
                      >
                        {c.mode}
                      </Badge>
                    </td>
                    <td className='p-2'>{c.tenant ?? '—'}</td>
                    <td className='p-2 text-right space-x-1'>
                      <Button
                        size='sm'
                        variant='ghost'
                        onClick={() => testMut.mutate(c.id)}
                        title={t('dbproxy.testPripojeni', 'Test připojení')}
                      >
                        <Zap size={14} />
                      </Button>
                      <Button
                        size='sm'
                        variant='ghost'
                        onClick={() => setEditTarget(c)}
                        title={t('common.edit', 'Upravit')}
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        size='sm'
                        variant='ghost'
                        onClick={() => {
                          if (confirm(`Smazat připojení "${c.label}"?`)) deleteMut.mutate(c.id);
                        }}
                        title={t('common.delete', 'Smazat')}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      {showAdd && <AddConnectionModal onClose={() => setShowAdd(false)} />}
      {editTarget && (
        <EditConnectionModal connection={editTarget} onClose={() => setEditTarget(null)} />
      )}
    </Card>
  );
}

function EditConnectionModal(
  { connection, onClose }: { connection: Connection; onClose: () => void },
) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const opts = (connection.options ?? {}) as Record<string, unknown>;
  const [form, setForm] = useState({
    label: connection.label,
    host: connection.host,
    port: connection.port,
    instanceName: typeof opts.instanceName === 'string' ? opts.instanceName : '',
    database: connection.database,
    username: connection.username,
    password: '', // prázdné = ponechat současné
    mode: connection.mode,
    tenant: connection.tenant ?? '',
    enabled: connection.enabled,
    encrypt: opts.encrypt !== false,
    trustServerCertificate: opts.trustServerCertificate !== false,
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  const updateMut = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        label: form.label,
        host: form.host,
        port: form.port,
        database: form.database,
        username: form.username,
        mode: form.mode,
        tenant: form.tenant || undefined,
        enabled: form.enabled,
        options: connection.dialect === 'mssql'
          ? {
            encrypt: form.encrypt,
            trustServerCertificate: form.trustServerCertificate,
            ...(form.instanceName ? { instanceName: form.instanceName } : {}),
          }
          : {},
      };
      if (form.password) body.password = form.password;
      return api.mcp<{ ok: boolean; error?: string }>('dbproxy.connections', {
        action: 'update',
        id: connection.id,
        ...body,
      });
    },
    onSuccess: (res) => {
      const r = res as { ok: boolean; error?: string };
      if (!r.ok) {
        toast.error(`Chyba: ${r.error ?? i18n.t('dbproxy.neznama', 'neznámá')}`);
        return;
      }
      toast.success(i18n.t('dbproxy.pripojeniUpraveno', 'Připojení upraveno'));
      qc.invalidateQueries({ queryKey: ['dbproxy-connections'] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Modal open onClose={onClose} title={`Upravit: ${connection.label}`} size='lg'>
      <div className='space-y-3'>
        <div className='grid grid-cols-2 gap-3'>
          <div>
            <Label>Label *</Label>
            <Input value={form.label} onChange={(e) => set('label', e.target.value)} />
          </div>
          <div>
            <Label>Dialekt</Label>
            <Input value={connection.dialect.toUpperCase()} disabled />
          </div>
        </div>
        <div className='grid grid-cols-3 gap-3'>
          <div className='col-span-2'>
            <Label>Host *</Label>
            <Input value={form.host} onChange={(e) => set('host', e.target.value)} />
          </div>
          <div>
            <Label>Port</Label>
            <Input
              type='number'
              value={form.port}
              onChange={(e) => set('port', Number(e.target.value))}
              disabled={connection.dialect === 'mssql' && Boolean(form.instanceName)}
            />
          </div>
        </div>
        {connection.dialect === 'mssql' && (
          <div>
            <Label>Instance name (volitelné)</Label>
            <Input
              value={form.instanceName}
              onChange={(e) => set('instanceName', e.target.value)}
              placeholder='SQLEXPRESS, MYCORE, …'
            />
          </div>
        )}
        <div className='grid grid-cols-2 gap-3'>
          <div>
            <Label>Databáze *</Label>
            <Input value={form.database} onChange={(e) => set('database', e.target.value)} />
          </div>
          <div>
            <Label>{t('dbproxy.uzivatel', 'Uživatel *')}</Label>
            <Input value={form.username} onChange={(e) => set('username', e.target.value)} />
          </div>
        </div>
        <div>
          <Label>Nové heslo</Label>
          <Input
            type='password'
            value={form.password}
            onChange={(e) => set('password', e.target.value)}
            placeholder={t('dbproxy.nechPrazdnePonechatSoucasne', 'Nech prázdné = ponechat současné')}
          />
          {connection.password_set && !form.password && (
            <p className='text-xs text-gray-500 mt-1'>
              {t('dbproxy.soucasneHesloJeNastavene', 'Současné heslo je nastavené v SecretStore. Vyplň jen když ho měníš.')}
            </p>
          )}
        </div>
        <div className='grid grid-cols-3 gap-3'>
          <div>
            <Label>{t('dbproxy.rezim', 'Režim')}</Label>
            <select
              className='w-full border rounded px-2 py-1.5 text-sm'
              value={form.mode}
              onChange={(e) => set('mode', e.target.value)}
            >
              <option value='read-only'>read-only</option>
              <option value='read-write'>read-write</option>
            </select>
          </div>
          <div>
            <Label>Tenant</Label>
            <Input value={form.tenant} onChange={(e) => set('tenant', e.target.value)} />
          </div>
          <div>
            <Label>Stav</Label>
            <label className='flex items-center gap-2 text-sm pt-2'>
              <input
                type='checkbox'
                checked={form.enabled}
                onChange={(e) => set('enabled', e.target.checked)}
              />
              enabled
            </label>
          </div>
        </div>
        {connection.dialect === 'mssql' && (
          <div>
            <button
              type='button'
              className='flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700'
              onClick={() => setShowAdvanced((v) => !v)}
            >
              <Settings size={12} />
              <ChevronRight
                size={12}
                className={`transition-transform ${showAdvanced ? 'rotate-90' : ''}`}
              />
              MSSQL options
            </button>
            {showAdvanced && (
              <div className='mt-2 space-y-2 pl-4 border-l-2 border-gray-200'>
                <label className='flex items-center gap-2 text-sm'>
                  <input
                    type='checkbox'
                    checked={form.encrypt}
                    onChange={(e) => set('encrypt', e.target.checked)}
                  />
                  encrypt (TLS)
                </label>
                <label className='flex items-center gap-2 text-sm'>
                  <input
                    type='checkbox'
                    checked={form.trustServerCertificate}
                    onChange={(e) => set('trustServerCertificate', e.target.checked)}
                  />
                  trustServerCertificate (= self-signed cert)
                </label>
              </div>
            )}
          </div>
        )}
        <div className='flex justify-end gap-2 pt-2'>
          <Button variant='secondary' onClick={onClose}>{t('common.cancel', 'Zrušit')}</Button>
          <Button
            variant='primary'
            onClick={() => updateMut.mutate()}
            disabled={updateMut.isPending}
          >
            {updateMut.isPending ? t('ui.saving', 'Ukládám…') : t('common.save', 'Uložit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function AddConnectionModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    label: '',
    dialect: 'mssql',
    host: '',
    port: 1433,
    instanceName: '',
    database: '',
    username: '',
    password: '',
    mode: 'read-only',
    tenant: '',
    encrypt: true,
    trustServerCertificate: true,
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  const addMut = useMutation({
    mutationFn: () =>
      api.mcp<{ ok: boolean; error?: string; connection?: Connection }>('dbproxy.connections', {
          action: 'add',
          label: form.label,
          dialect: form.dialect,
          host: form.host,
          port: form.port,
          database: form.database,
          username: form.username,
          password: form.password,
          mode: form.mode,
          tenant: form.tenant || undefined,
          options: form.dialect === 'mssql'
            ? {
              encrypt: form.encrypt,
              trustServerCertificate: form.trustServerCertificate,
              ...(form.instanceName ? { instanceName: form.instanceName } : {}),
            }
            : {},
        },
      ),
    onSuccess: (res) => {
      const r = res as { ok: boolean; error?: string };
      if (!r.ok) {
        toast.error(`Chyba: ${r.error ?? i18n.t('common.unknownError', 'neznámá chyba')}`);
        return;
      }
      toast.success(i18n.t('dbproxy.pripojeniVytvoreno', 'Připojení vytvořeno'));
      qc.invalidateQueries({ queryKey: ['dbproxy-connections'] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const canSubmit = form.label && form.host && form.database && form.username && form.password;

  return (
    <Modal open onClose={onClose} title={t('dbproxy.noveDbPripojeni', 'Nové DB připojení')} size='lg'>
      <div className='space-y-3'>
        <div className='grid grid-cols-2 gap-3'>
          <div>
            <Label>Label *</Label>
            <Input
              value={form.label}
              onChange={(e) => set('label', e.target.value)}
              placeholder='legacy-mssql'
            />
          </div>
          <div>
            <Label>Dialekt *</Label>
            <select
              className='w-full border rounded px-2 py-1.5 text-sm'
              value={form.dialect}
              onChange={(e) => {
                const d = e.target.value;
                set('dialect', d);
                if (d === 'mssql') set('port', 1433);
                else if (d === 'postgres') set('port', 5432);
                else if (d === 'mysql') set('port', 3306);
              }}
            >
              <option value='mssql'>MSSQL</option>
              <option value='postgres'>PostgreSQL</option>
              <option value='mysql' disabled>MySQL (not implemented)</option>
            </select>
          </div>
        </div>
        <div className='grid grid-cols-3 gap-3'>
          <div className='col-span-2'>
            <Label>Host *</Label>
            <Input
              value={form.host}
              onChange={(e) => set('host', e.target.value)}
              placeholder='sql.firma.cz'
            />
          </div>
          <div>
            <Label>Port {form.dialect === 'mssql' && form.instanceName ? '' : '*'}</Label>
            <Input
              type='number'
              value={form.port}
              onChange={(e) => set('port', Number(e.target.value))}
              disabled={form.dialect === 'mssql' && Boolean(form.instanceName)}
              title={form.instanceName ? t('dbproxy.priNamedInstanceIgnorovan', 'Při named instance ignorován (SQL Browser dohledá)') : ''}
            />
          </div>
        </div>
        {form.dialect === 'mssql' && (
          <div>
            <Label>Instance name (volitelné)</Label>
            <Input
              value={form.instanceName}
              onChange={(e) => set('instanceName', e.target.value)}
              placeholder={t('dbproxy.sqlexpressMssqlserverNamedInstance', 'SQLEXPRESS, MSSQLSERVER, … (= named instance přes SQL Browser :1434/UDP)')}
            />
            <p className='text-xs text-gray-500 mt-1'>
              {t('dbproxy.pokudVyplnisPortSe', 'Pokud vyplníš, port se ignoruje — driver dohledá dynamický port přes SQL Browser na UDP 1434 cílového hostu.')}
            </p>
          </div>
        )}
        <div className='grid grid-cols-2 gap-3'>
          <div>
            <Label>Databáze *</Label>
            <Input value={form.database} onChange={(e) => set('database', e.target.value)} />
          </div>
          <div>
            <Label>{t('dbproxy.uzivatel', 'Uživatel *')}</Label>
            <Input value={form.username} onChange={(e) => set('username', e.target.value)} />
          </div>
        </div>
        <div>
          <Label>Heslo *</Label>
          <Input
            type='password'
            value={form.password}
            onChange={(e) => set('password', e.target.value)}
            placeholder={t('dbproxy.uloziSeDoSecretstore', 'Uloží se do SecretStore, ne do DB')}
          />
        </div>
        <div className='grid grid-cols-2 gap-3'>
          <div>
            <Label>{t('dbproxy.rezim', 'Režim')}</Label>
            <select
              className='w-full border rounded px-2 py-1.5 text-sm'
              value={form.mode}
              onChange={(e) => set('mode', e.target.value)}
            >
              <option value='read-only'>read-only (write endpointy zablokované)</option>
              <option value='read-write'>read-write</option>
            </select>
          </div>
          <div>
            <Label>Tenant</Label>
            <Input
              value={form.tenant}
              onChange={(e) => set('tenant', e.target.value)}
              placeholder='(volitelné)'
            />
          </div>
        </div>
        {form.dialect === 'mssql' && (
          <div>
            <button
              type='button'
              className='flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700'
              onClick={() => setShowAdvanced((v) => !v)}
            >
              <Settings size={12} />
              <ChevronRight
                size={12}
                className={`transition-transform ${showAdvanced ? 'rotate-90' : ''}`}
              />
              MSSQL options
            </button>
            {showAdvanced && (
              <div className='mt-2 space-y-2 pl-4 border-l-2 border-gray-200'>
                <label className='flex items-center gap-2 text-sm'>
                  <input
                    type='checkbox'
                    checked={form.encrypt}
                    onChange={(e) => set('encrypt', e.target.checked)}
                  />
                  encrypt (TLS)
                </label>
                <label className='flex items-center gap-2 text-sm'>
                  <input
                    type='checkbox'
                    checked={form.trustServerCertificate}
                    onChange={(e) => set('trustServerCertificate', e.target.checked)}
                  />
                  trustServerCertificate (= self-signed cert)
                </label>
              </div>
            )}
          </div>
        )}
        <div className='flex justify-end gap-2 pt-2'>
          <Button variant='secondary' onClick={onClose}>{t('common.cancel', 'Zrušit')}</Button>
          <Button
            variant='primary'
            onClick={() => addMut.mutate()}
            disabled={!canSubmit || addMut.isPending}
          >
            {addMut.isPending ? t('dbproxy.vytvarim', 'Vytvářím…') : t('ui.create', 'Vytvořit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Explorer Tab ──────────────────────────────────────────────────────────────

function ExplorerTab() {
  const { t } = useTranslation();
  const [selectedConn, setSelectedConn] = useState('');
  const [selectedTable, setSelectedTable] = useState<{ schema: string | null; name: string } | null>(
    null,
  );

  const { data: connsData } = useQuery({
    queryKey: ['dbproxy-connections'],
    queryFn: () =>
      api.mcp<{ ok: boolean; connections?: Connection[] }>('dbproxy.connections', { action: 'list' }),
    staleTime: 15_000,
  });
  const conns = (connsData?.connections ?? []).filter((c) => c.enabled);

  const { data: tablesData, isLoading: tablesLoading } = useQuery({
    queryKey: ['dbproxy-tables', selectedConn],
    queryFn: () =>
      api.mcp<{ ok: boolean; rows?: IntrospectRow[]; error?: string }>('dbproxy.introspect', {
        connection_id: selectedConn,
        type: 'tables',
      }),
    enabled: !!selectedConn,
    staleTime: 60_000,
  });
  const tables = tablesData?.rows ?? [];

  const tableQualified = selectedTable
    ? (selectedTable.schema ? `${selectedTable.schema}.${selectedTable.name}` : selectedTable.name)
    : '';

  const { data: columnsData } = useQuery({
    queryKey: ['dbproxy-columns', selectedConn, tableQualified],
    queryFn: () => {
      return api.mcp<{ ok: boolean; rows?: IntrospectRow[] }>('dbproxy.introspect', {
        connection_id: selectedConn,
        type: 'columns',
        table: selectedTable!.name,
        ...(selectedTable!.schema ? { schema: selectedTable!.schema } : {}),
      });
    },
    enabled: !!selectedConn && !!selectedTable,
    staleTime: 60_000,
  });
  const columns = columnsData?.rows ?? [];

  const { data: sampleData, isLoading: sampleLoading } = useQuery({
    queryKey: ['dbproxy-sample', selectedConn, tableQualified],
    queryFn: () =>
      api.mcp<{ ok: boolean; rows?: Record<string, unknown>[] }>('dbproxy.sample', {
        connection_id: selectedConn,
        table: tableQualified,
        limit: 10,
      }),
    enabled: !!selectedConn && !!selectedTable,
    staleTime: 30_000,
  });
  const sampleRows = sampleData?.rows ?? [];

  return (
    <div className='space-y-4'>
      <Card>
        <CardContent className='py-3 px-4'>
          <Label className='mr-2'>{t('dbproxy.pripojeni2', 'Připojení:')}</Label>
          <select
            className='border rounded px-2 py-1 text-sm'
            value={selectedConn}
            onChange={(e) => {
              setSelectedConn(e.target.value);
              setSelectedTable(null);
            }}
          >
            <option value=''>— vyber —</option>
            {conns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label} ({c.dialect})
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      {selectedConn && (
        <div className='grid grid-cols-3 gap-4'>
          {/* Table list */}
          <Card className='col-span-1'>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <TableIcon size={16} /> Tabulky <Badge>{tables.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className='max-h-[600px] overflow-y-auto'>
              {tablesLoading && (
                <div className='text-sm text-gray-500 p-2'>{t('common.loading', 'Načítám…')}</div>
              )}
              {!tablesLoading && tables.length === 0 && (
                <EmptyState
                  icon={<TableIcon size={24} />}
                  title={t('dbproxy.zadneTabulkyNeboIntrospect', 'Žádné tabulky (nebo introspect selhal)')}
                />
              )}
              <ul className='space-y-1'>
                {tables.map((t) => {
                  const isSel = selectedTable?.name === t.name &&
                    selectedTable?.schema === (t.schema ?? null);
                  return (
                    <li key={`${t.schema}.${t.name}`}>
                      <button
                        type='button'
                        className={`w-full text-left px-2 py-1 rounded text-sm font-mono ${
                          isSel ? 'bg-blue-100 text-blue-900' : 'hover:bg-gray-50'
                        }`}
                        onClick={() =>
                          setSelectedTable({
                            schema: (t.schema as string) ?? null,
                            name: t.name as string,
                          })}
                      >
                        {t.schema ? `${t.schema}.` : ''}
                        {t.name}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>

          {/* Columns + sample */}
          <div className='col-span-2 space-y-4'>
            {selectedTable && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className='text-sm font-mono'>
                      {tableQualified} <Badge>{columns.length} {t('dbproxy.sloupcu', 'sloupců')}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className='max-h-[200px] overflow-y-auto'>
                    <table className='w-full text-xs'>
                      <thead>
                        <tr className='border-b'>
                          <th className='text-left p-1'>Sloupec</th>
                          <th className='text-left p-1'>Typ</th>
                          <th className='text-left p-1'>Nullable</th>
                        </tr>
                      </thead>
                      <tbody>
                        {columns.map((col) => (
                          <tr key={col.column as string} className='border-b'>
                            <td className='p-1 font-mono'>{col.column as string}</td>
                            <td className='p-1'>{col.data_type as string}</td>
                            <td className='p-1'>{col.is_nullable as string}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className='text-sm'>
                      Sample <Badge>{sampleRows.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className='max-h-[300px] overflow-auto'>
                    {sampleLoading && (
                      <div className='text-sm text-gray-500 p-2'>{t('common.loading', 'Načítám…')}</div>
                    )}
                    {!sampleLoading && sampleRows.length > 0 && (
                      <table className='w-full text-xs'>
                        <thead>
                          <tr className='border-b'>
                            {Object.keys(sampleRows[0]!).slice(0, 8).map((k) => (
                              <th key={k} className='text-left p-1 font-mono'>{k}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sampleRows.map((row, i) => (
                            <tr key={i} className='border-b'>
                              {Object.keys(sampleRows[0]!).slice(0, 8).map((k) => (
                                <td key={k} className='p-1 font-mono truncate max-w-[150px]'>
                                  {String(row[k] ?? '')}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
